/**
 * Provider Config API Service
 *
 * Service functions for interacting with the provider configuration API endpoints.
 */

import { apiClient } from "./api";
import type {
  LLMProviderConfigRead,
  LLMManagedProviderSecretRead,
  CreateProviderConfigRequest,
} from "@/types/provider";

const API_PREFIX = "/api/v1/redteam";

/**
 * List all provider configurations for the current user.
 */
export async function listUserProviderConfigs(): Promise<LLMProviderConfigRead[]> {
  const response = await apiClient.get<{
    data: LLMProviderConfigRead[];
    total_count: number;
    has_more: boolean;
    page: number;
    items_per_page: number;
    total_pages: number;
  }>(`${API_PREFIX}/provider-config`);
  return response.data || [];
}

/**
 * List all managed provider secrets available to users.
 */
export async function listManagedProviderSecrets(): Promise<LLMManagedProviderSecretRead[]> {
  return apiClient.get<LLMManagedProviderSecretRead[]>(`${API_PREFIX}/provider-config/managed`);
}

/**
 * Create a new provider configuration for the current user.
 * This stores the API key securely (encrypted) for future use.
 *
 * @param config - Provider configuration with credentials
 * @returns The created provider configuration
 */
export async function createProviderConfig(
  config: CreateProviderConfigRequest
): Promise<LLMProviderConfigRead> {
  return apiClient.post<LLMProviderConfigRead>(`${API_PREFIX}/provider-config`, config);
}

/**
 * Find an existing provider config for a given provider type.
 * Returns the first active config found, or undefined if none exists.
 */
export function findExistingProviderConfig(
  providerType: string,
  userConfigs: LLMProviderConfigRead[]
): LLMProviderConfigRead | undefined {
  return userConfigs.find((config) => config.provider_type === providerType && config.is_active);
}

/**
 * Extract provider type from a foundation model ID.
 * Examples:
 * - "openai:gpt-4o" -> "openai"
 * - "anthropic:messages:claude-opus-4-5-20251101" -> "anthropic"
 * - "google:gemini-2.5-pro" -> "google"
 * - "vertex:gemini-2.5-pro" -> "vertex"
 */
export function extractProviderFromModelId(modelId: string): string | null {
  const parts = modelId.split(":");
  if (parts.length < 2) {
    return null;
  }
  return parts[0];
}

/**
 * Resolve which credential source to use for a given provider type.
 * Priority: Inline API key > User's stored credentials > Platform-managed credentials
 *
 * @param providerType - Provider type (e.g., "openai", "anthropic", "vertex")
 * @param userConfigs - User's stored provider configurations
 * @param managedSecrets - Platform-managed provider secrets
 * @param inlineApiKey - Optional inline API key override
 * @returns Object indicating which credential source to use
 */
export function resolveCredentialsForProvider(
  providerType: string,
  userConfigs: LLMProviderConfigRead[],
  managedSecrets: LLMManagedProviderSecretRead[],
  inlineApiKey?: string
): {
  source: "inline" | "user" | "managed" | "none";
  configId?: string;
  managedSecretId?: string;
} {
  // Priority 1: Inline API key override
  if (inlineApiKey && inlineApiKey.trim() !== "") {
    return { source: "inline" };
  }

  // Priority 2: User's stored credentials
  const userConfig = userConfigs.find(
    (config) => config.provider_type === providerType && config.is_active
  );
  if (userConfig) {
    return {
      source: "user",
      configId: userConfig.uuid,
    };
  }

  // Priority 3: Platform-managed credentials
  const managedSecret = managedSecrets.find(
    (secret) => secret.provider_type === providerType && secret.is_active
  );
  if (managedSecret) {
    return {
      source: "managed",
      managedSecretId: managedSecret.uuid,
    };
  }

  // No credentials available
  return { source: "none" };
}

/**
 * Check if a model requires an API key to be provided by the user.
 * Currently, only Anthropic models require user-provided API keys.
 *
 * @param modelId - Foundation model ID (e.g., "anthropic:messages:claude-opus-4-5-20251101")
 * @returns True if the model requires an API key
 */
export function requiresApiKey(modelId: string): boolean {
  return modelId.startsWith("anthropic:");
}

/**
 * Check if credentials are available for a provider.
 * For Anthropic models, requires either an inline API key or a saved provider config (not managed secrets).
 * For other providers, checks inline API key, saved config, or platform-managed secrets.
 *
 * This function delegates to resolveCredentialsForProvider to ensure consistency between
 * credential resolution and availability checks.
 *
 * @param providerType - Provider type (e.g., "openai", "anthropic", "google")
 * @param inlineApiKey - Inline API key value
 * @param userConfigs - User's stored provider configurations
 * @param managedSecrets - Platform-managed provider secrets
 * @returns True if credentials are available
 */
export function hasCredentialsForProvider(
  providerType: string | null,
  inlineApiKey: string,
  userConfigs: LLMProviderConfigRead[],
  managedSecrets: LLMManagedProviderSecretRead[]
): boolean {
  if (!providerType || providerType.trim() === "") {
    return false;
  }

  // Delegate credential resolution to ensure consistency
  const resolved = resolveCredentialsForProvider(
    providerType.trim(),
    userConfigs,
    managedSecrets,
    inlineApiKey.trim() || undefined
  );

  // For Anthropic, only allow inline or user credentials (not managed secrets)
  if (providerType.trim() === "anthropic") {
    return resolved.source === "inline" || resolved.source === "user";
  }

  // For other providers, any valid credential source is acceptable
  return resolved.source !== "none";
}
